import { load } from 'js-yaml';
import * as zod from 'zod';

import * as log from '@sb/log';

import { EWELLIX_CONFIGURATIONS } from './consts';

const ns = log.namespace('EwellixLiftTLT.configLoader');

const ETC_CONFIG_PATH =
  '/etc/standardbots/configuration/ewellix_configuration.yaml';

const ActuatorOverrideZ = zod.object({
  index: zod.number().int().optional(),
  minEncoderFlanks: zod.number().int(),
  maxEncoderFlanks: zod.number().int(),
});

const StrokeOverrideZ = zod.object({
  baseHeightMeters: zod.number().optional(),
  strokeMeters: zod.number().optional(),
  calibrated: zod.boolean().optional(),
  actuators: zod.array(ActuatorOverrideZ).optional(),
});

const FileSchemaZ = zod.record(StrokeOverrideZ);

type FileSchema = zod.infer<typeof FileSchemaZ>;

let cachedConfig: typeof EWELLIX_CONFIGURATIONS | null = null;

async function readEtcYaml(): Promise<FileSchema | null> {
  try {
    // Lazy-load fs to avoid bundler issues
    const fs = await import('node:fs/promises');

    const raw = await fs.readFile(ETC_CONFIG_PATH, 'utf-8');
    const doc = (load(raw) || {}) as unknown;
    const parsed = FileSchemaZ.safeParse(doc);

    if (!parsed.success) {
      log.warn(ns`load`, 'Invalid ewellix YAML schema', {
        issues: parsed.error.issues.map((i) => i.message).join('; '),
      });

      return null;
    }

    log.info(ns`load`, 'Loaded ewellix configuration YAML from /etc', {
      path: ETC_CONFIG_PATH,
    });

    return parsed.data;
  } catch (e: any) {
    log.warn(ns`load`, 'Failed to read ewellix YAML config, using defaults', {
      path: ETC_CONFIG_PATH,
      message: e?.message,
    });

    return null;
  }
}

function recomputeEncoderFlanksPerMeter(
  minFlanks: number,
  maxFlanks: number,
  strokeMeters: number,
): number {
  return (maxFlanks - minFlanks) / strokeMeters;
}

function mergeOverrides(
  base: typeof EWELLIX_CONFIGURATIONS,
  file: FileSchema | null,
): typeof EWELLIX_CONFIGURATIONS {
  if (!file) return base;

  const merged: any = { ...base };

  for (const [strokeKey, override] of Object.entries(file)) {
    const current = (merged as any)[strokeKey];

    if (!current) {
      // Unknown stroke variant; skip to be safe
      log.warn(ns`merge`, 'Unknown stroke variant in YAML, skipping', {
        strokeKey,
      });

      continue;
    }

    const baseHeightMeters =
      override.baseHeightMeters ?? current.baseHeightMeters;

    const strokeMeters = override.strokeMeters ?? current.strokeMeters;

    const calibrated =
      typeof override.calibrated === 'boolean'
        ? override.calibrated
        : current.calibrated ?? false;

    // Start with existing actuators
    const updatedActuators = current.actuators.map((act: any) => ({ ...act }));

    if (override.actuators && override.actuators.length > 0) {
      override.actuators.forEach((o, idx) => {
        const targetIndex = o.index ?? idx; // default to array order if no index provided
        const existing = updatedActuators[targetIndex];

        if (!existing) return;

        const minEncoderFlanks =
          o.minEncoderFlanks ?? existing.minEncoderFlanks;

        const maxEncoderFlanks =
          o.maxEncoderFlanks ?? existing.maxEncoderFlanks;

        updatedActuators[targetIndex] = {
          ...existing,
          minEncoderFlanks,
          maxEncoderFlanks,
          strokeMeters: existing.strokeMeters, // actuator-level stroke portion remains
          encoderFlanksPerMeter: recomputeEncoderFlanksPerMeter(
            minEncoderFlanks,
            maxEncoderFlanks,
            existing.strokeMeters,
          ),
        };
      });
    }

    (merged as any)[strokeKey] = {
      baseHeightMeters,
      strokeMeters,
      calibrated,
      actuators: updatedActuators,
    };
  }

  return merged as typeof EWELLIX_CONFIGURATIONS;
}

export async function loadEwellixConfigurations(): Promise<
  typeof EWELLIX_CONFIGURATIONS
> {
  if (cachedConfig) return cachedConfig;

  const defaults = EWELLIX_CONFIGURATIONS;
  const file = await readEtcYaml();
  cachedConfig = mergeOverrides(defaults, file);

  return cachedConfig;
}

export function getEwellixConfigurationsSync(): typeof EWELLIX_CONFIGURATIONS {
  // If we've loaded and cached a merged config, return it; otherwise default to constants.
  return cachedConfig ?? EWELLIX_CONFIGURATIONS;
}
